/**
 * TanStack Query hook for `GET /customers` (#42).
 *
 * Powers the customer-filter dropdown on the invoice list (#42) and the
 * customer-select on the upload dialog (#44). Both consumers want the same
 * narrow shape (id+name), so we share one cache entry and let
 * `staleTime` keep the round-trip count low: a workspace's customer list
 * changes at human pace, not request pace.
 */
import {
  CustomerOptionListSchema,
  CustomerSupplierOptionListSchema,
} from '@invoice/shared/schemas';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import type { z } from 'zod';
import { apiFetch } from '../api';

export type CustomerOptionList = z.infer<typeof CustomerOptionListSchema>;
export type CustomerSupplierOptionList = z.infer<typeof CustomerSupplierOptionListSchema>;

export const CUSTOMER_LIST_KEY = ['customers', 'list'] as const;
export const CUSTOMER_SUPPLIER_LIST_KEY = ['customers', 'suppliers'] as const;

interface FetchCustomerListOptions {
  search?: string | undefined;
  signal?: AbortSignal | undefined;
}

interface UseCustomerListOptions {
  search?: string | undefined;
  enabled?: boolean | undefined;
}

export function customerListKey(search?: string) {
  return [...CUSTOMER_LIST_KEY, search?.trim() ?? ''] as const;
}

export async function fetchCustomerList(
  options: FetchCustomerListOptions = {},
): Promise<CustomerOptionList> {
  const search = options.search?.trim();
  return apiFetch('/customers', {
    schema: CustomerOptionListSchema,
    ...(options.signal ? { signal: options.signal } : {}),
    ...(search && search.length > 0 ? { searchParams: { q: search } } : {}),
  });
}

export function useCustomerList(
  options: UseCustomerListOptions = {},
): UseQueryResult<CustomerOptionList, Error> {
  const search = options.search?.trim();
  return useQuery({
    queryKey: customerListKey(search),
    queryFn: ({ signal }) => fetchCustomerList({ search, signal }),
    enabled: options.enabled ?? true,
    // Customers change at human pace; refetch only after 5 minutes idle.
    staleTime: 5 * 60_000,
  });
}

interface FetchCustomerSupplierListOptions {
  customerId?: string | null | undefined;
  search?: string | undefined;
  signal?: AbortSignal | undefined;
}

interface UseCustomerSupplierListOptions {
  customerId?: string | null | undefined;
  search?: string | undefined;
  enabled?: boolean | undefined;
}

export function customerSupplierListKey(customerId?: string | null, search?: string) {
  return [...CUSTOMER_SUPPLIER_LIST_KEY, customerId ?? null, search?.trim() ?? ''] as const;
}

export async function fetchCustomerSupplierList(
  options: FetchCustomerSupplierListOptions,
): Promise<CustomerSupplierOptionList> {
  const search = options.search?.trim();
  const customerId = options.customerId?.trim();
  const path =
    customerId && customerId.length > 0
      ? `/customers/${encodeURIComponent(customerId)}/suppliers`
      : '/customers/suppliers';
  return apiFetch(path, {
    schema: CustomerSupplierOptionListSchema,
    ...(options.signal ? { signal: options.signal } : {}),
    ...(search && search.length > 0 ? { searchParams: { q: search } } : {}),
  });
}

export function useCustomerSupplierList(
  options: UseCustomerSupplierListOptions = {},
): UseQueryResult<CustomerSupplierOptionList, Error> {
  const search = options.search?.trim();
  const customerId = options.customerId ?? null;
  return useQuery({
    queryKey: customerSupplierListKey(customerId, search),
    queryFn: ({ signal }) =>
      fetchCustomerSupplierList({ customerId: customerId as string | null, search, signal }),
    enabled: options.enabled ?? true,
    staleTime: 5 * 60_000,
  });
}
